import { useState, useEffect } from 'react';
import { lmsApi } from '@/shared/api/common';
import { usePhase2ReportStore } from '@/entities/phase2-report/model/report.store';
import { exceptionCurriBook } from '@/shared/utils/common';
import { useCreateStore } from '@/entities/create';

export type CurriculumListType = 'total-list' | 'eng-list' | 'spc-list';

export interface MetaCurriData {
    treeData: CurriculumProps[];
    totalCnt: number;
}

/**
 * 커리큘럼 데이터를 tree 구조로 가공하여 반환하는 커스텀 훅 (react-query 미사용)
 *
 * @param type 커리큘럼 리스트 타입 ('total-list', 'eng-list', 'spc-list')
 * @returns { data, loading, error }
 */
const useMetaCurriData = (type: CurriculumListType, curTextbookId?: number) => {
    const [data, setData] = useState<MetaCurriData>({ treeData: [], totalCnt: 0 });
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);

    const { sCurribookId } = useCreateStore();
    const { textbkId } = usePhase2ReportStore();
    const maxDepth = exceptionCurriBook.includes(sCurribookId!) ? 3 : 2;

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await lmsApi._getTextbookMetaCrcuList({
                    textbkId: curTextbookId ?? textbkId ?? 0,
                });

                if (res) {
                    const _list = [...res];
                    const totalCnt = _list.length;

                    // 재귀적으로 자식 데이터를 구성하는 함수
                    const getProps = (item: any): any => {
                        const _children = _list.filter((c: any) => {
                            if (c.parentId !== item.id) return false;
                            // curTextbookId가 없거나 maxDepth < 3이면 depth 3 항목 제외
                            if (c.depth === 3 && !(curTextbookId && maxDepth === 3)) return false;
                            return true;
                        });
                        const children = _children.map((c: any) => getProps(c));
                        return {
                            id: item.id,
                            parent: item.parentId,
                            text: item.val,
                            depth: item.depth,
                            children,
                            is_open: false,
                            lastPosition: false,
                            key: 0,
                            crculId: 0,
                        };
                    };

                    // depth가 1인 항목을 루트로 하여 트리 구조 생성
                    const rootList = _list.filter((c: any) => c.depth == 1);
                    const treeData = rootList.map((c: any) => getProps(c));
                    setData({ treeData, totalCnt });
                } else {
                    setData({ treeData: [], totalCnt: 0 });
                }
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [type, textbkId, curTextbookId]);

    return { data, loading, error };
};

export default useMetaCurriData;
